import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { CalculatorInput, CalculatorResult, CalculatorResultsGrid } from '@/components/calculators'
import {
  calculateMonthlyPayment,
  calculateMonthlyCashFlow,
  calculateCashOnCash,
  calculateCapRate,
  calculateDSCR,
  calculateBreakEvenRent,
  calculateGRM,
  formatCurrency,
  formatPercent,
} from '@/lib/calculators/formulas'

export default function RentalCalculatorPage() {
  const [purchasePrice, setPurchasePrice] = useState(170000)
  const [downPaymentPercent, setDownPaymentPercent] = useState(25)
  const [closingCosts, setClosingCosts] = useState(5000)
  const [rehabCosts, setRehabCosts] = useState(0)
  const [interestRate, setInterestRate] = useState(7)
  const [loanTerm, setLoanTerm] = useState(30)
  const [monthlyRent, setMonthlyRent] = useState(2400)
  const [vacancyRate, setVacancyRate] = useState(8)
  const [propertyTaxes, setPropertyTaxes] = useState(3000)
  const [insurance, setInsurance] = useState(1200)
  const [maintenance, setMaintenance] = useState(200)
  const [management, setManagement] = useState(200)
  const [otherExpenses, setOtherExpenses] = useState(0)

  const downPayment = purchasePrice * (downPaymentPercent / 100)
  const loanAmount = purchasePrice - downPayment
  const totalCashIn = downPayment + closingCosts + rehabCosts
  const monthlyDebt = calculateMonthlyPayment(loanAmount, interestRate, loanTerm)

  const monthlyTaxes = propertyTaxes / 12
  const monthlyInsurance = insurance / 12
  const totalMonthlyExpenses = monthlyTaxes + monthlyInsurance + maintenance + management + otherExpenses

  const monthlyCashFlow = calculateMonthlyCashFlow(monthlyRent, vacancyRate, totalMonthlyExpenses, monthlyDebt)
  const annualCashFlow = monthlyCashFlow * 12

  const effectiveGrossIncome = monthlyRent * 12 * (1 - vacancyRate / 100)
  const annualExpenses = totalMonthlyExpenses * 12
  const noi = effectiveGrossIncome - annualExpenses

  const cashOnCash = calculateCashOnCash(annualCashFlow, totalCashIn)
  const capRate = calculateCapRate(noi, purchasePrice)
  const dscr = calculateDSCR(noi / 12, monthlyDebt)
  const grm = calculateGRM(purchasePrice, monthlyRent * 12)
  const breakEvenRent = calculateBreakEvenRent(totalMonthlyExpenses + monthlyDebt, vacancyRate)

  const expenseRatio = (annualExpenses / effectiveGrossIncome) * 100

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 py-12">
      <Link to="/resources" className="text-amber-600 hover:underline">
        &larr; Back to Resources
      </Link>
      <h1 className="text-3xl font-bold">Full Rental Property Analyzer</h1>

      <Card>
        <CardContent className="p-6">
          <p>
            The full analysis. Enter a property's details and see every metric that matters: cash flow, cash-on-cash,
            cap rate, DSCR, break-even rent, and expense ratio.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Acquisition</h2>

            <CalculatorInput label="Purchase Price" value={purchasePrice} onChange={setPurchasePrice} prefix="$" />
            <CalculatorInput
              label="Down Payment"
              value={downPaymentPercent}
              onChange={setDownPaymentPercent}
              suffix="%"
            />
            <CalculatorInput label="Closing Costs" value={closingCosts} onChange={setClosingCosts} prefix="$" />
            <CalculatorInput label="Rehab Costs" value={rehabCosts} onChange={setRehabCosts} prefix="$" />

            <div className="text-muted-foreground text-sm">
              Loan: {formatCurrency(loanAmount)} | Cash in: {formatCurrency(totalCashIn)}
            </div>

            <Separator />

            <h2 className="text-lg font-semibold">Financing</h2>

            <CalculatorInput label="Interest Rate" value={interestRate} onChange={setInterestRate} suffix="%" />

            <div className="space-y-1.5">
              <span className="text-sm font-medium">Loan Term</span>
              <div className="flex gap-2" role="group" aria-label="Loan Term">
                {[15, 20, 30].map((term) => (
                  <button
                    key={term}
                    onClick={() => setLoanTerm(term)}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      loanTerm === term ? 'bg-amber-500 text-white' : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {term} years
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            <h2 className="text-lg font-semibold">Income</h2>

            <CalculatorInput label="Monthly Gross Rent" value={monthlyRent} onChange={setMonthlyRent} prefix="$" />
            <CalculatorInput label="Vacancy Rate" value={vacancyRate} onChange={setVacancyRate} suffix="%" />

            <Separator />

            <h2 className="text-lg font-semibold">Monthly Expenses</h2>

            <CalculatorInput
              label="Property Taxes (Annual)"
              value={propertyTaxes}
              onChange={setPropertyTaxes}
              prefix="$"
            />
            <CalculatorInput label="Insurance (Annual)" value={insurance} onChange={setInsurance} prefix="$" />
            <CalculatorInput label="Maintenance" value={maintenance} onChange={setMaintenance} prefix="$" />
            <CalculatorInput label="Management" value={management} onChange={setManagement} prefix="$" />
            <CalculatorInput
              label="Other (HOA, utilities, etc.)"
              value={otherExpenses}
              onChange={setOtherExpenses}
              prefix="$"
            />

            <div className="text-muted-foreground text-sm">
              Total monthly expenses: {formatCurrency(totalMonthlyExpenses)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Cash Flow</h2>

            <CalculatorResult label="Monthly Cash Flow" value={formatCurrency(monthlyCashFlow)} highlight />

            <CalculatorResultsGrid>
              <CalculatorResult label="Annual Cash Flow" value={formatCurrency(annualCashFlow)} />
              <CalculatorResult label="Monthly Debt Service" value={formatCurrency(monthlyDebt)} />
            </CalculatorResultsGrid>

            <Separator />

            <h2 className="text-lg font-semibold">Return Metrics</h2>

            <CalculatorResultsGrid>
              <CalculatorResult
                label="Cash-on-Cash"
                value={formatPercent(cashOnCash)}
                subtext="Annual cash flow / cash invested"
              />
              <CalculatorResult label="Cap Rate" value={formatPercent(capRate)} subtext="NOI / purchase price" />
              <CalculatorResult
                label="DSCR"
                value={`${dscr.toFixed(2)}x`}
                subtext={dscr >= 1.25 ? 'Strong' : dscr >= 1.0 ? 'Marginal' : 'Below breakeven'}
              />
              <CalculatorResult label="GRM" value={`${grm.toFixed(1)}x`} subtext="Price / annual rent" />
            </CalculatorResultsGrid>

            <Separator />

            <h2 className="text-lg font-semibold">Break-Even Analysis</h2>

            <CalculatorResultsGrid>
              <CalculatorResult
                label="Break-Even Rent"
                value={formatCurrency(breakEvenRent)}
                subtext="Min rent to cover all costs"
              />
              <CalculatorResult
                label="Expense Ratio"
                value={formatPercent(expenseRatio)}
                subtext="Expenses / effective income"
              />
              <CalculatorResult label="Annual NOI" value={formatCurrency(noi)} subtext="Before debt service" />
              <CalculatorResult
                label="Effective Gross Income"
                value={formatCurrency(effectiveGrossIncome)}
                subtext="After vacancy"
              />
            </CalculatorResultsGrid>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
